import * as THREE from "three";

export interface PolaroidOptions {
  photo: HTMLImageElement;
  /** World-space width of the polaroid (paper frame, not photo). */
  width?: number;
  /** Optional handwritten caption painted into the bottom margin. */
  caption?: string;
}

/**
 * A small textured plane that renders a single photo inside a paper frame
 * with a soft drop shadow. Designed to live as a child of the card root and
 * be dragged across the inside-left page when the card is open.
 *
 * The texture is rendered into a canvas larger than the visible polaroid so
 * the drop shadow can extend outside the paper without being clipped; the
 * plane geometry matches the same expanded size and we let the transparent
 * margin handle the shadow falloff.
 */
export class Polaroid {
  readonly mesh: THREE.Mesh;
  readonly width: number;
  readonly height: number;

  constructor(opts: PolaroidOptions) {
    const width = opts.width ?? 0.6;
    // Polaroid paper aspect: 540 wide x 660 tall canvas pixels = 0.818
    const PAPER_ASPECT = 540 / 660;
    const height = width / PAPER_ASPECT;
    this.width = width;
    this.height = height;

    const { tex, expandRatio } = makePolaroidTexture(opts.photo, opts.caption);
    // The mesh extends past the paper to give the drop shadow room to render.
    const planeW = width * expandRatio;
    const planeH = height * expandRatio;

    const geom = new THREE.PlaneGeometry(planeW, planeH);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      transparent: true,
      alphaTest: 0.01,
      roughness: 0.8,
      metalness: 0,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(geom, mat);
    this.mesh.userData.polaroid = this; // for raycast lookup
  }

  dispose() {
    this.mesh.geometry.dispose();
    const m = this.mesh.material as THREE.MeshStandardMaterial;
    if (m.map) m.map.dispose();
    m.dispose();
  }
}

// ---------------------------------------------------------------------------
// Canvas: paint a polaroid (paper + photo + optional caption) into an
// oversized canvas so the drop shadow extends past the paper edge.
// ---------------------------------------------------------------------------

const PAPER_W = 540;
const PAPER_H = 660;
const SHADOW_PAD = 60; // extra pixels around the paper for shadow falloff
const CANVAS_W = PAPER_W + SHADOW_PAD * 2;
const CANVAS_H = PAPER_H + SHADOW_PAD * 2;

function makePolaroidTexture(
  photo: HTMLImageElement,
  caption?: string,
): { tex: THREE.CanvasTexture; expandRatio: number } {
  const c = document.createElement("canvas");
  c.width = CANVAS_W;
  c.height = CANVAS_H;
  const ctx = c.getContext("2d")!;

  // The paper rectangle, centered in the oversized canvas.
  const px = SHADOW_PAD;
  const py = SHADOW_PAD;

  // Drop shadow first (drawn as a filled rect with shadow on, then we paint
  // the paper on top with shadow off so the photo isn't double-shadowed).
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.45)";
  ctx.shadowBlur = 26;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 14;
  ctx.fillStyle = "rgba(0, 0, 0, 0)"; // shadow only
  // Trick: draw a solid rect then clear it leaves the shadow behind in canvas.
  ctx.fillStyle = "#000";
  ctx.fillRect(px, py, PAPER_W, PAPER_H);
  ctx.restore();

  // Paper
  ctx.fillStyle = "#fffaf0";
  ctx.fillRect(px, py, PAPER_W, PAPER_H);

  // Photo window inside the paper frame.
  const pad = 28;
  const captionPad = caption ? 90 : 60;
  const phx = px + pad;
  const phy = py + pad;
  const phw = PAPER_W - pad * 2;
  const phh = PAPER_H - pad * 2 - captionPad;
  drawImageCover(ctx, photo, phx, phy, phw, phh);

  // A subtle inner border on the photo edge for definition.
  ctx.strokeStyle = "rgba(0, 0, 0, 0.08)";
  ctx.lineWidth = 1;
  ctx.strokeRect(phx + 0.5, phy + 0.5, phw - 1, phh - 1);

  // Caption in handwriting (optional).
  if (caption) {
    ctx.fillStyle = "#3a2c1a";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "48px 'AnishHand', 'Georgia', cursive";
    ctx.fillText(caption, px + PAPER_W / 2, phy + phh + captionPad / 2 + 10);
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;

  // The plane needs to scale to the full canvas, not just the paper, so the
  // shadow is visible. Caller multiplies its world width by this ratio.
  const expandRatio = CANVAS_W / PAPER_W;
  return { tex, expandRatio };
}

/** object-fit: cover semantics — scale to fully cover the dst rect, crop overflow. */
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  const ir = img.width / img.height;
  const dr = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = img.width;
  let sh = img.height;
  if (ir > dr) {
    sw = img.height * dr;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / dr;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}
